// Use Vite proxy in dev, or explicit URL in staging/prod
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api';

/**
 * Pre-release UX: Staging builds (VITE_ENVIRONMENT=Staging) and local Vite dev only.
 * Production builds must set VITE_ENVIRONMENT=Production so these stay off until sign-off.
 */
export const HIGH_FIVES_ENABLED =
  import.meta.env.VITE_ENVIRONMENT === 'Staging' || import.meta.env.DEV;

/** Same gating as High Fives — expense claims are not released to Production yet. */
export const EXPENSE_CLAIMS_ENABLED =
  import.meta.env.VITE_ENVIRONMENT === 'Staging' || import.meta.env.DEV;

/**
 * Staging/local hint for Add User: new accounts get Auth:DefaultNewUserPassword (same as seeded users).
 * Not shown in Production (Microsoft sign-in only).
 */
export const SHOW_STAGING_DEFAULT_PASSWORD_HINT =
  import.meta.env.VITE_ENVIRONMENT === 'Staging' || import.meta.env.DEV;

/** Matches Auth:DefaultNewUserPassword / seed import password for Staging and Development. */
export const STAGING_DEFAULT_USER_PASSWORD = 'Pass@word123!';

/** Dispatched on every API request. Session service listens to reset inactivity timer. */
export const API_ACTIVITY_EVENT = 'apiActivity';

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    CONFIG: '/auth/config',
  },
  ROLES: {
    BASE: '/roles',
    BY_ID: (id: number) => `/roles/${id}`,
    SUBMIT_FOR_APPROVAL: (id: string) => `/roles/${id}/submit-for-approval`,
    APPROVE: (id: string) => `/roles/${id}/approve`,
    APPLY_OBJECTIVE_WEIGHTS: (id: string) => `/roles/${id}/apply-objective-weights`,
  },
  REVIEW_CYCLES: {
    BASE: '/review-cycles',
    BY_ID: (id: number) => `/review-cycles/${id}`,
    START: (id: number) => `/review-cycles/${id}/start`,
    CLOSE: (id: number) => `/review-cycles/${id}/close`,
    OUTSTANDING: (id: string) => `/review-cycles/${id}/outstanding`,
    CHECK_AND_CREATE: '/review-cycles/check-and-create',
    CREATE_REVIEW: (id: string) => `/review-cycles/${id}/reviews`,
  },
  REVIEWS: {
    BASE: '/reviews',
    MANAGED: '/reviews/managed',
    DASHBOARD_REPORTING: (cycleId?: string) =>
      cycleId ? `/reviews/dashboard-reporting?cycleId=${encodeURIComponent(cycleId)}` : '/reviews/dashboard-reporting',
    BY_ID: (id: number) => `/reviews/${id}`,
    SELF_REVIEW: (id: number) => `/reviews/${id}/self-review`,
    PEER_VIEW: (id: string) => `/reviews/${id}/peer-view`,
    SELF_REVIEW_SUBMIT: (id: string) => `/reviews/${id}/self-review/submit`,
    PEER_RATINGS: (id: number) => `/reviews/${id}/peer-ratings`,
    MANAGER_REVIEW: (id: number) => `/reviews/${id}/manager-review`,
    FINALISE: (id: number) => `/reviews/${id}/finalise`,
    APPROVE_PEER_NOMINATION: (nominationId: string) => `/reviews/peer-nominations/${nominationId}/approve`,
    ADD_PEER_RATERS: (id: string) => `/reviews/${id}/peer-nominations`,
    AUDIT_TRAIL: (id: string) => `/reviews/${id}/audit-trail`,
    AUDIT_LOG: '/reviews/audit-log',
    EXCLUDE_REQUEST: (id: string) => `/reviews/${id}/exclude/request`,
    EXCLUDE_APPROVE: (id: string) => `/reviews/${id}/exclude/approve`,
    EXCLUDE_DENY: (id: string) => `/reviews/${id}/exclude/deny`,
    /** Admin-only; lightweight count for sidebar / My Dashboard. */
    DISPUTED_COUNT: '/reviews/disputed/count',
  },
  DASHBOARDS: {
    COMPLETION: '/dashboards/completion',
    RATINGS_DISTRIBUTION: '/dashboards/ratings-distribution',
    TRENDS: '/dashboards/trends',
  },
  REPORTS: {
    TEAM: (managerId: number) => `/reports/team/${managerId}`,
    EMPLOYEE: (employeeId: number) => `/reports/employee/${employeeId}`,
  },
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    ME: '/users/me',
    MANAGERS: '/users/managers',
    PEERS: '/users/peers',
    ORGANOGRAM: (view: string) => `/users/organogram?view=${view}`,
    MY_TEAM: '/users/team-roster',
    UPDATE_TEAM_MEMBER: (employeeId: string) => `/users/team-roster/${employeeId}`,
    AUDIT_EVENTS: '/users/audit-events',
    CONSOLIDATED_AUDIT_EVENTS: '/users/consolidated-audit-events',
    ACKNOWLEDGE_ROLE_TEMPLATE_CHANGE_AS_MANAGER: (employeeId: string) => `/users/acknowledge-role-template-change-as-manager/${employeeId}`,
    ACKNOWLEDGE_ROLE_TEMPLATE_CHANGE_AS_EMPLOYEE: '/users/acknowledge-role-template-change-as-employee',
    EMAIL_PREFERENCES: '/users/me/email-preferences',
    EMAIL_PREFERENCES_REAFFIRM_OPT_OUT: '/users/me/email-preferences/reaffirm-opt-out',
    DISPLAY_TIMEZONE: '/users/me/display-timezone',
    OUTSTANDING_TASKS: '/users/me/outstanding-tasks',
    GOVERNANCE_STATUS: '/users/me/governance-status',
    ACKNOWLEDGE_GOVERNANCE: '/users/me/acknowledge-governance',
    PROFILE_SETUP_STATUS: '/users/me/profile-setup-status',
    /** Workforce snapshot by scope view: team, department, businessunit, or all. */
    WORKFORCE_INSIGHTS: (view: 'team' | 'department' | 'businessunit' | 'all') =>
      `/users/workforce-insights?view=${encodeURIComponent(view)}`,
    /** Team snapshot for direct reports with organisation benchmark metrics. */
    TEAM_WORKFORCE_INSIGHTS: '/users/team-workforce-insights',
  },
  USER_MANAGEMENT: {
    BASE: '/user-management',
    BY_ID: (id: string) => `/user-management/${id}`,
  },
  REFERENCE_DATA: {
    COMPANIES: {
      BASE: '/ReferenceData/companies',
      BY_ID: (id: string) => `/ReferenceData/companies/${id}`,
    },
    BUSINESS_UNITS: {
      BASE: '/ReferenceData/business-units',
      BY_ID: (id: string) => `/ReferenceData/business-units/${id}`,
    },
    DEPARTMENTS: {
      BASE: '/ReferenceData/departments',
      BY_ID: (id: string) => `/ReferenceData/departments/${id}`,
    },
    EXPENSE_CATEGORIES: {
      BASE: '/ReferenceData/expense-categories',
      BY_ID: (id: string) => `/ReferenceData/expense-categories/${id}`,
    },
    EXPENSE_VAT_OPTIONS: '/ReferenceData/expense-vat-options',
  },
  EMAIL_LOGS: {
    BASE: '/EmailLogs',
    BY_ID: (id: string) => `/EmailLogs/${id}`,
  },
  SYSTEM: {
    FISCAL_YEAR_CURRENT: '/System/fiscal-year/current',
    FISCAL_YEAR_QUARTER_RANGE: (fiscalYear: number, quarter: number) =>
      `/System/fiscal-year/quarter-range?fiscalYear=${fiscalYear}&quarter=${quarter}`,
    FISCAL_YEAR_EVALUATE: (date: string) =>
      `/System/fiscal-year/evaluate?date=${encodeURIComponent(date)}`,
    /** Same alignment as IDP and review cycles (UseCalendarYear). Use when matching cycles to IDP year. */
    REVIEW_YEAR_EVALUATE: (date: string) =>
      `/System/review-year/evaluate?date=${encodeURIComponent(date)}`,
    EXCHANGE_RATES: '/System/exchange-rates',
    /** Authenticated: rows in PublicHolidays + PublicHolidayYearSyncs for the organisation country. */
    PUBLIC_HOLIDAY_DATASET: '/System/public-holiday-dataset',
    /** Administrator only: re-merge holidays from Nager for the standard year window (not missing-years-only). */
    PUBLIC_HOLIDAY_SYNC: '/System/public-holiday-sync',
  },
  CHECK_INS: {
    BASE: '/checkins',
    CONFIG_BY_EMPLOYEE: (employeeId: string) => `/checkins/config/${employeeId}`,
    CONFIG_TEAM: '/checkins/config/team',
    CONFIG_MY: '/checkins/config/my',
    MY: '/checkins/my',
    BY_ID: (id: string) => `/checkins/${id}`,
    ACKNOWLEDGE_SCHEDULING_MANAGER: (id: string) => `/checkins/${id}/acknowledge-scheduling-manager`,
    ACKNOWLEDGE_SCHEDULING_EMPLOYEE: (id: string) => `/checkins/${id}/acknowledge-scheduling-employee`,
    CONFIRM_OCCURRENCE_MANAGER: (id: string) => `/checkins/${id}/confirm-occurrence-manager`,
    CONFIRM_OCCURRENCE_EMPLOYEE: (id: string) => `/checkins/${id}/confirm-occurrence-employee`,
    MARK_DECISION: (id: string) => `/checkins/${id}/mark-decision`,
    MARK_MISSED: (id: string) => `/checkins/${id}/mark-missed`,
    AUDIT_TRAIL: (id: string) => `/checkins/${id}/audit-trail`,
    CONFIG_AUDIT_TRAIL: (configId: string) => `/checkins/config/${configId}/audit-trail`,
    AUDIT_LOG: '/checkins/audit-log',
    RUN_SCHEDULING: '/checkins/run-scheduling',
    RUN_REMINDERS: '/checkins/run-reminders',
    DASHBOARD_EMPLOYEE: '/checkins/dashboard/employee',
    DASHBOARD_MANAGER: '/checkins/dashboard/manager',
    DASHBOARD_ADMIN: '/checkins/dashboard/admin',
    MANAGER_PREFERENCE: '/checkins/manager-preference',
    MANAGER_PREFERENCE_OPT_OUT: '/checkins/manager-preference/opt-out',
    MANAGER_PREFERENCE_OPT_IN: '/checkins/manager-preference/opt-in',
    MANAGER_PREFERENCE_REAFFIRM: '/checkins/manager-preference/reaffirm',
  },
  LEAVE: {
    AUDIT_LOG: '/leave/audit-log',
    RECALCULATE_WORKING_DAYS: '/leave/recalculate-working-days',
    BALANCES: '/leave/balances',
    BALANCE_BY_ID: (id: string) => `/leave/balances/${id}`,
    MY_REPORT: '/leave/my-report',
    TEAM_REPORT: '/leave/team-report',
    ORGANISATION_REPORT: '/leave/organisation-report',
    TEAMS: '/leave/teams',
    STATUTORY_AT_RISK: (fiscalYear?: number) =>
      fiscalYear != null ? `/leave/statutory-at-risk?fiscalYear=${fiscalYear}` : '/leave/statutory-at-risk',
    STATUTORY_ANNUAL_STATUS: (fiscalYear?: number) =>
      fiscalYear != null ? `/leave/statutory-annual-status?fiscalYear=${fiscalYear}` : '/leave/statutory-annual-status',
    TEAM_STATUTORY_ANNUAL_STATUS: (fiscalYear?: number, directReportsOnly?: boolean) => {
      const params = new URLSearchParams();
      if (fiscalYear != null) params.set('fiscalYear', String(fiscalYear));
      if (directReportsOnly) params.set('directReportsOnly', 'true');
      const q = params.toString();
      return q ? `/leave/team-statutory-annual-status?${q}` : '/leave/team-statutory-annual-status';
    },
    MY_STATUTORY_ANNUAL_STATUS: (fiscalYear?: number) =>
      fiscalYear != null ? `/leave/my-statutory-annual-status?fiscalYear=${fiscalYear}` : '/leave/my-statutory-annual-status',
    FAMILY_RESPONSIBILITY_USAGE: (fiscalYear?: number) =>
      fiscalYear != null ? `/leave/family-responsibility-usage?fiscalYear=${fiscalYear}` : '/leave/family-responsibility-usage',
    SICK_LEAVE_USAGE: (dateFrom?: string, dateTo?: string) => {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const q = params.toString();
      return q ? `/leave/sick-leave-usage?${q}` : '/leave/sick-leave-usage';
    },
  },
  LEAVE_REQUESTS: {
    LEAVE_TYPES: '/leave/requests/leave-types',
    ENTITLEMENT_MESSAGE: (
      leaveTypeId: string,
      fromDate: string,
      toDate: string,
      onBehalfOfEmployeeId?: string,
      isHalfDay?: boolean
    ) => {
      const params = new URLSearchParams({
        leaveTypeId,
        fromDate,
        toDate,
      });
      if (onBehalfOfEmployeeId) params.set('onBehalfOfEmployeeId', onBehalfOfEmployeeId);
      if (isHalfDay === true) params.set('isHalfDay', 'true');
      return `/leave/requests/entitlement-message?${params.toString()}`;
    },
    BASE: '/leave/requests',
    BY_ID: (id: string) => `/leave/requests/${id}`,
    SUBMIT: (id: string) => `/leave/requests/${id}/submit`,
    APPROVE: (id: string) => `/leave/requests/${id}/approve`,
    WITHDRAW: (id: string) => `/leave/requests/${id}/withdraw`,
    UPDATE: (id: string) => `/leave/requests/${id}`,
    APPROVE_HR: (id: string) => `/leave/requests/${id}/approve-hr`,
    PENDING_FOR_HR: '/leave/requests/pending-for-hr',
    MY: '/leave/requests/my',
    DEPARTMENT_PENDING: '/leave/requests/department-pending',
    PENDING_FOR_MANAGER: '/leave/requests/pending-for-manager',
    EVIDENCE: (requestId: string) => `/leave/requests/${requestId}/evidence`,
    EVIDENCE_UPLOAD: (requestId: string) => `/leave/requests/${requestId}/evidence/upload`,
    EVIDENCE_DOWNLOAD: (requestId: string, documentId: string) => `/leave/requests/${requestId}/evidence/${documentId}/download`,
    EVIDENCE_DELETE: (requestId: string, documentId: string) => `/leave/requests/${requestId}/evidence/${documentId}`,
  },
  EXPENSE_CLAIMS: {
    BASE: '/expense-claims',
    BY_ID: (id: string) => `/expense-claims/${id}`,
    SUBMIT: (id: string) => `/expense-claims/${id}/submit`,
    PENDING_FOR_MANAGER: '/expense-claims/pending-for-manager',
    TEAM: '/expense-claims/team',
    ORGANISATION: '/expense-claims/organisation',
    APPROVE: (id: string) => `/expense-claims/${id}/approve`,
    REJECT: (id: string) => `/expense-claims/${id}/reject`,
    WITHDRAW: (id: string) => `/expense-claims/${id}/withdraw`,
    REVISE: (id: string) => `/expense-claims/${id}/revise`,
    DOCUMENTS: (claimId: string) => `/expense-claims/${claimId}/documents`,
    DOCUMENTS_UPLOAD: (claimId: string) => `/expense-claims/${claimId}/documents/upload`,
    DOCUMENT_DOWNLOAD: (claimId: string, documentId: string) => `/expense-claims/${claimId}/documents/${documentId}/download`,
    DOCUMENT_DELETE: (claimId: string, documentId: string) => `/expense-claims/${claimId}/documents/${documentId}`,
    AUDIT_LOG: '/expense-claims/audit-log',
  },
  HIGH_FIVES: {
    BASE: '/high-fives',
    BY_ID: (id: string) => `/high-fives/${id}`,
    CURRENT_WEEK: '/high-fives/weeks/current',
    WEEK: (weekStart: string) => `/high-fives/weeks/${encodeURIComponent(weekStart)}`,
    ADMIN_CURRENT_WEEK: '/high-fives/admin/weeks/current',
    ADMIN_PUBLISH_WEEK: (weekStart: string) => `/high-fives/admin/weeks/${encodeURIComponent(weekStart)}/publish`,
    LEADERBOARD: '/high-fives/leaderboard',
    RECIPIENT_OPTIONS: '/high-fives/recipient-options',
  },
  AGENT: {
    STATUS: '/agent/status',
    CHAT: '/agent/chat',
    RESET: '/agent/chat/reset',
  },
};

